import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, LoaderCircle, RefreshCw } from 'lucide-react';
import { PersonaState } from '../components/PersonaShared';
import { personaInterviewApi, resolvePersonaApiErrorMessage } from '../services/personaService';
import {
  InsightReportActionItem,
  InsightReportEvidence,
} from '../components/InsightReportShared';
import '../styles/persona.scss';

export default function InterviewInsightPage() {
  const navigate = useNavigate();
  const { interviewId } = useParams();
  const [interview, setInterview] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [lastGeneratedAt, setLastGeneratedAt] = useState(null);
  const [autoGenerateAttempted, setAutoGenerateAttempted] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    setAutoGenerateAttempted(false);
    try {
      const data = await personaInterviewApi.getInterview(interviewId);
      setInterview(data);
      setInsight(data?.insights || null);
    } catch (err) {
      setError(resolvePersonaApiErrorMessage(err, '인사이트 보고서를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [interviewId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const canRegenerate = !lastGeneratedAt || Date.now() - lastGeneratedAt > 3 * 60 * 1000;
  const evidenceLookup = useMemo(
    () => new Map((insight?.evidence || []).map((item) => [item.id, item])),
    [insight],
  );
  const hasResultData = (interview?.results || []).some((row) => row.status !== 'failed');
  const generatedAt = insight?.generatedAt || insight?.generated_at;

  const handleGenerate = useCallback(async (force = false) => {
    if (generating || (force && !canRegenerate)) return;
    setGenerating(true);
    setError('');
    try {
      const payload = await personaInterviewApi.generateInsights(interviewId, { force });
      if (payload?.insight) setInsight(payload.insight);
      if (payload?.interview) setInterview(payload.interview);
      setLastGeneratedAt(Date.now());
    } catch (err) {
      setError(resolvePersonaApiErrorMessage(err, '인사이트 생성에 실패했습니다.'));
    } finally {
      setGenerating(false);
    }
  }, [canRegenerate, generating, interviewId]);

  useEffect(() => {
    if (loading || insight || !hasResultData || generating || autoGenerateAttempted) return;
    setAutoGenerateAttempted(true);
    void handleGenerate(false);
  }, [autoGenerateAttempted, generating, handleGenerate, hasResultData, insight, loading]);

  const handleBack = () => navigate(`/tests/interview/${interviewId}`);

  const topbar = (
    <header className="insight-report-topbar">
      <div className="insight-report-topbar__inner">
        <button type="button" className="insight-report-back" onClick={handleBack} aria-label="결과로 돌아가기">
          <ChevronLeft size={20} />
        </button>
        <div className="insight-report-topbar__copy">
          <p>Insight Report</p>
          <h1>{interview?.name || '인사이트 보고서'}</h1>
        </div>
        <div className="insight-report-topbar__actions">
          <button
            type="button"
            className="insight-report-regen"
            onClick={() => handleGenerate(Boolean(insight))}
            disabled={generating || !hasResultData || (insight && !canRegenerate)}
          >
            {generating ? <LoaderCircle size={16} className="insight-panel__spin" /> : <RefreshCw size={16} />}
            {insight ? '다시 생성' : '보고서 생성'}
          </button>
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <main className="insight-report-page">
        {topbar}
        <section className="insight-report-body">
          <PersonaState type="loading" title="불러오는 중" message="인사이트 보고서를 준비하고 있습니다." />
        </section>
      </main>
    );
  }

  if (error && !interview) {
    return (
      <main className="insight-report-page">
        {topbar}
        <section className="insight-report-body">
          <PersonaState title="보고서를 열 수 없습니다" message={error} />
        </section>
      </main>
    );
  }

  return (
    <main className="insight-report-page">
      {topbar}
      <section className="insight-report-body">
        {insight ? (
          <div className="insight-report-hero">
            <div className="insight-report-hero__copy">
              <h2>{insight.oneLineReview || '인사이트 보고서'}</h2>
            </div>
          </div>
        ) : null}

        {error ? <p className="insight-panel__error">{error}</p> : null}

        {!insight && generating ? (
          <div className="insight-report-loading-card insight-report-loading-card--page">
            <LoaderCircle size={22} className="insight-panel__spin" />
            <div>
              <strong>보고서를 생성하고 있습니다.</strong>
              <p>인터뷰 코멘트를 분석하여 핵심 개선안을 정리하고 있습니다.</p>
            </div>
          </div>
        ) : null}

        {!insight && !generating ? (
          <PersonaState
            title="아직 보고서가 없습니다"
            message={hasResultData ? '보고서 생성 버튼을 눌러 인사이트를 만들어 주세요.' : '완료된 인터뷰 결과가 필요합니다.'}
          />
        ) : null}

        {insight ? (
          <>
            {(insight.themes || []).length > 0 ? (
              <section className="insight-report-section">
                <div className="insight-report-section__head">
                  <h3>주요 내용 요약</h3>
                  {generatedAt ? <time dateTime={generatedAt}>{new Date(generatedAt).toLocaleString('ko-KR')}</time> : null}
                </div>
                <div className="insight-report-list">
                  {insight.themes.map((theme) => (
                  (() => {
                    const selectedEvidence = (theme.evidenceIds || [])
                      .map((id) => evidenceLookup.get(id))
                      .filter(Boolean);
                    const selectedPersonaIds = new Set(selectedEvidence.map((evidence) => String(evidence.personaId || '')));
                    const originalVoices = selectedEvidence.some((evidence) => evidence.kind === 'quote')
                      ? selectedEvidence.filter((evidence) => evidence.kind === 'quote').slice(0, 2)
                      : (insight.evidence || [])
                        .filter((evidence) => evidence.kind === 'quote' && selectedPersonaIds.has(String(evidence.personaId || '')))
                        .slice(0, 2);
                    return (
                      <article key={theme.title} className="insight-report-item">
                        <div className="insight-report-item__head">
                          <h4>{theme.title}</h4>
                        </div>
                        <div className="insight-report-item__body">
                          <p>{theme.summary}</p>
                          <InsightReportEvidence
                            items={originalVoices}
                            sourceLabel="인터뷰 응답"
                          />
                        </div>
                      </article>
                    );
                  })()
                  ))}
                </div>
              </section>
            ) : null}

            <section className="insight-report-section">
              <div className="insight-report-section__head">
                <h3>개선 제안</h3>
              </div>
              <div className="insight-report-list">
                {(insight.actionItems || []).map((item) => (
                  <InsightReportActionItem
                    key={`${item.priority}-${item.title}`}
                    item={item}
                  />
                ))}
              </div>
            </section>
          </>
        ) : null}
      </section>
    </main>
  );
}
